import React, { useState, useEffect } from 'react';
import { Modal, Spin, message, Timeline, Card, Empty } from 'antd';
import { MapContainer, Marker, Popup, Polyline } from 'react-leaflet';
import YandexTileLayer from '../../../shared/components/maps/YandexTileLayer';
import { EnvironmentOutlined, ClockCircleOutlined } from '@ant-design/icons';
import L from 'leaflet';
import dayjs from 'dayjs';
import fieldActivityService from '../../../../services/field-activity/field-activity.service';
import { LocationLog } from '../../../../types/field.types';
import 'leaflet/dist/leaflet.css';

interface ActivityLocationHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  activityId: number;
  activityTitle: string;
}

const ActivityLocationHistoryModal: React.FC<ActivityLocationHistoryModalProps> = ({
  visible,
  onClose,
  activityId,
  activityTitle
}) => {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<LocationLog[]>([]);

  useEffect(() => {
    if (visible && activityId) {
      fetchLocationHistory();
    }
  }, [visible, activityId]);

  const fetchLocationHistory = async () => {
    try {
      setLoading(true);
      const response = await fieldActivityService.getActivityLocationHistory(activityId);
      console.log(response);
      setLocations(response || []);
    } catch (error) {
      message.error('Konum geçmişi yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Başlangıç marker icon (yeşil)
  const startIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [30, 49],
    iconAnchor: [15, 49],
    popupAnchor: [0, -49],
    shadowSize: [41, 41]
  });

  // Bitiş marker icon (kırmızı)
  const endIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [30, 49],
    iconAnchor: [15, 49],
    popupAnchor: [0, -49],
    shadowSize: [41, 41]
  });

  // Orta nokta marker icon (mavi) - aktiviteyle aynı renk
  const middleIcon = new L.Icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [0, -41],
    shadowSize: [41, 41]
  });

  // Harita merkezi hesapla
  const getMapCenter = (): [number, number] => {
    if (locations.length === 0) return [39.9334, 32.8597]; // Ankara default
    
    const firstLocation = locations[0];
    return [parseFloat(firstLocation.latitude), parseFloat(firstLocation.longitude)];
  };

  // Polyline için koordinatlar
  const polylinePositions = locations.map(loc => [
    parseFloat(loc.latitude),
    parseFloat(loc.longitude)
  ] as [number, number]);

  // Marker icon seç
  const getMarkerIcon = (index: number) => {
    if (index === 0) return startIcon;
    if (index === locations.length - 1) return endIcon;
    return middleIcon;
  };

  // Marker label
  const getMarkerLabel = (index: number) => {
    if (index === 0) return '🟢 Başlangıç';
    if (index === locations.length - 1) return '🔴 Bitiş';
    return `📍 Konum ${index + 1}`;
  };

  return (
    <Modal
      title={
        <div className="d-flex align-items-center">
          <EnvironmentOutlined className="me-2 text-primary" style={{ fontSize: '20px' }} />
          <div>
            <div style={{ fontSize: '16px', fontWeight: 600 }}>{activityTitle}</div>
            <div style={{ fontSize: '12px', fontWeight: 400, color: '#8c8c8c' }}>
              Konum Geçmişi
            </div>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1200}
      style={{ top: 20 }}
    >
      <Spin spinning={loading}>
        {locations.length === 0 ? (
          <Empty
            description="Bu aktivite için konum kaydı bulunamadı"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <div className="row">
            {/* Harita - Sol taraf */}
            <div className="col-md-8">
              <Card
                title={
                  <span>
                    <EnvironmentOutlined className="me-2" />
                    Rota Haritası ({locations.length} nokta)
                  </span>
                }
                bordered={false}
                style={{ height: '600px' }}
              >
                <MapContainer
                  center={getMapCenter()}
                  zoom={13}
                  style={{ height: '520px', width: '100%', borderRadius: '8px' }}
                >
                  <YandexTileLayer />

                  {/* Polyline - Rota çizgisi */}
                  {polylinePositions.length > 1 && (
                    <Polyline
                      positions={polylinePositions}
                      pathOptions={{
                        color: '#1890ff',
                        weight: 3,
                        opacity: 0.7
                      }}
                    />
                  )}

                  {/* Marker'lar */}
                  {locations.map((location, index) => (
                    <Marker
                      key={location.id}
                      position={[parseFloat(location.latitude), parseFloat(location.longitude)]}
                      icon={getMarkerIcon(index)}
                    >
                      <Popup>
                        <div style={{ minWidth: '200px' }}>
                          <h6 className="mb-2">{getMarkerLabel(index)}</h6>
                          <div className="small">
                            <div className="mb-1">
                              <ClockCircleOutlined className="me-1" />
                              {dayjs(location.timestamp).format('DD.MM.YYYY HH:mm:ss')}
                            </div>
                            <div className="mb-1">
                              <EnvironmentOutlined className="me-1" />
                              {parseFloat(location.latitude).toFixed(6)}, {parseFloat(location.longitude).toFixed(6)}
                            </div>
                          </div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </Card>
            </div>

            {/* Timeline - Sağ taraf */}
            <div className="col-md-4">
              <Card
                title={
                  <span>
                    <ClockCircleOutlined className="me-2" />
                    Zaman Çizelgesi
                  </span>
                }
                bordered={false}
                style={{ height: '600px', overflowY: 'auto' }}
              >
                <Timeline mode="left">
                  {locations.map((location, index) => (
                    <Timeline.Item
                      key={location.id}
                      color={index === 0 ? 'green' : index === locations.length - 1 ? 'red' : 'blue'}
                      label={dayjs(location.timestamp).format('HH:mm:ss')}
                    >
                      <div className="mb-2">
                        <strong>{getMarkerLabel(index)}</strong>
                      </div>
                      <div className="small text-muted">
                        <div>{dayjs(location.timestamp).format('DD.MM.YYYY')}</div>
                        <div className="mt-1">
                          Konum: {parseFloat(location.latitude).toFixed(4)}, {parseFloat(location.longitude).toFixed(4)}
                        </div>
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            </div>
          </div>
        )}
      </Spin>

      <style>{`
        .ant-modal-body {
          padding: 16px;
        }
        .leaflet-container {
          font-family: inherit;
        }
      `}</style>
    </Modal>
  );
};

export default ActivityLocationHistoryModal;

